import * as React from 'react';
import * as acl from 'common/utils/acl';

import { AppState } from 'common/models/AppState';
import { Member } from 'common/api/models/Member';
import { UserPermissions } from 'common/api/models/UserPermissions';
import { OrgAccess } from 'common/api/models/OrgAccess';

import { AddressBlock } from 'common/components/AddressBlock';
import { Datetime } from 'common/components/Datetime';
import { EditableTags } from 'common/components/EditableTags';
import { Email } from 'common/components/Email';
import { Phone } from 'common/components/Phone';
import { MemberLink } from 'common/components/link/MemberLink';
import { Badge } from 'common/components/Badge';
import { Tag } from 'common/api/models/Tag';
import { Feature } from 'common/features/Feature';
import { canViewBadges } from 'common/features/canViewBadges';
import { ChapterPosition } from 'common/api/models/ChapterPosition';
import { LinkedinLink } from 'common/components/link/LinkedinLink';
import {
  getFunctionalRoleLabel,
  getOrganizationLevelLabel,
  getSegmentLabel,
  getSubsegmentLabel,
} from 'common/utils/memberConstants';

export interface UsersDetailInfoProps {
  member: Member;
  appState: AppState;
  chapterPositions: ChapterPosition[];
  onUpdateTags: (tagIds: string[]) => void;
  onUpdateAssignedTags: (tagIds: string[]) => void;
  onUpdateInternalTags: (tagIds: string[]) => void;
}

export class UsersDetailInfo extends React.Component<UsersDetailInfoProps> {
  pluckChapterHistory(data: ChapterPosition[]) {
    let arr = [];
    for (var chapterPosition of data) {
      var year = new Date(chapterPosition.startDate ? chapterPosition.startDate : '').getFullYear();
      arr.push(year + ' ' + chapterPosition.positionName);
    }
    return arr.join(', ');
  }
  render() {
    const {
      appState: { entities },
      member,
      onUpdateTags,
      onUpdateInternalTags,
      onUpdateAssignedTags,
    } = this.props;
    const profileUrl = member.slug ? `${process.env.REACT_APP_WEB_URL}/${member.slug}` : undefined;

    return (
      <div style={{ display: 'flex', flexDirection: 'row', gap: 20, justifyContent: 'space-between' }}>
        <div className="col col-md-6">
          <div className="row" style={{ marginTop: 20 }}>
            <div className="col-4 text-right">
              <b>Member Type</b>
            </div>
            <div className="col">{member.memberType}</div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Chapter</b>
            </div>
            <div className="col">
              {member.chapterId && entities.chapter[member.chapterId] && entities.chapter[member.chapterId].name}
              {!member.chapterId && 'None/Foreign'}
            </div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Chapter Role</b>
            </div>
            <div className="col">
              {(!member.chapterPosition || member.chapterPosition!.roleName === Member.ChapterRole.NONE) && 'None'}
              {member.chapterPosition && member.chapterPosition!.roleName === Member.ChapterRole.MEMBER && 'Member'}
              {member.chapterPosition &&
                member.chapterPosition!.roleName === Member.ChapterRole.OFFICER &&
                'Officer (' + member.chapterPosition!.positionName + ')'}
            </div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Wiki Access</b>
            </div>
            <div className="col">{member.vars.wiki ? member.vars.wiki : 'NONE'}</div>
          </div>
          {member && (
            <div className="row">
              <div className="col-4 text-right">
                <b>Wiki Renewal Date</b>
              </div>
              <div className="col">
                {member.wikiExpireDate ? <Datetime at={member.wikiExpireDate} format="MMMM D, YYYY" /> : 'NONE'}
              </div>
            </div>
          )}
          <div className="row">
            <div className="col-4 text-right">
              <b>Global Connect Access</b>
            </div>
            <div className="col">{member.vars.globalConnect ? member.vars.globalConnect : 'NONE'}</div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Certification</b>
            </div>
            <div className="col">{member.vars.certification === 'NONE' ? 'None' : member.vars.certification}</div>
          </div>
          {member && (
            <div className="row">
              <div className="col-4 text-right">
                <b>Certification Renewal Date</b>
              </div>
              <div className="col">
                <Datetime at={member.certificationExpireDate} format="MMMM D, YYYY" />
              </div>
            </div>
          )}
          {member && (
            <div className="row">
              <div className="col-4 text-right">
                <b>Renewal Date</b>
              </div>
              <div className="col">
                <Datetime at={member.expireDate} format="MMMM D, YYYY" />
              </div>
            </div>
          )}
          {member && (
            <div className="row">
              <div className="col-4 text-right">
                <b>Renewal Type</b>
              </div>
              <div className="col">
                {member.renewType && member.renewType === Member.RenewType.AUTO
                  ? 'Auto-Renew'
                  : member.renewType && member.renewType === Member.RenewType.MANUAL
                    ? 'Manual'
                    : 'Invoice'}
              </div>
            </div>
          )}

          <div className="row" style={{ marginTop: 20 }}>
            <div className="col-4 text-right">
              <b>Email</b>
            </div>
            <div className="col">
              <Email email={member.email!} />
            </div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Secondary Email</b>
            </div>
            <div className="col">
              <Email email={member.secondaryEmail!} />
            </div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Home Phone</b>
            </div>
            <div className="col">
              <Phone phone={member.homePhone} />
            </div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Work Phone</b>
            </div>
            <div className="col">
              <Phone phone={member.workPhone} />
            </div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Mobile Phone</b>
            </div>
            <div className="col">
              <Phone phone={member.mobilePhone} />
            </div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>LinkedIn</b>
            </div>
            <div className="col">
              <LinkedinLink member={member} />
            </div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Home Address</b>
            </div>
            <div className="col">
              <AddressBlock address={member.homeAddress} />
            </div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Work Address</b>
            </div>
            <div className="col">
              <AddressBlock address={member.workAddress} />
            </div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Mailing Address</b>
            </div>
            <div className="col">
              <AddressBlock address={member.shippingAddress} />
            </div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Billing Address</b>
            </div>
            <div className="col">
              <AddressBlock address={member.billingAddress} />
            </div>
          </div>
        </div>

        <div className="col col-md-6">
          <div className="row" style={{ marginTop: 20 }}>
            <div className="col-4 text-right">
              <b>Orgs</b>
            </div>
            <div className="col">
              {member.orgs!.length === 0 && <div>None</div>}
              {member.orgs!.map(o => (
                <div key={o.memberId}>
                  <MemberLink member={entities.member[o.memberId!]} />{' '}
                  {o.access === OrgAccess.Access.ADMIN && <Badge type="primary" text="Admin" />}
                  {o.access === OrgAccess.Access.NORMAL && <Badge type="info" text="Normal" />}
                </div>
              ))}
            </div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Job Title</b>
            </div>
            <div className="col">{member.jobTitle ? member.jobTitle : <span>None</span>}</div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Segment</b>
            </div>
            <div className="col">{getSegmentLabel(member.segment)}</div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>SubSegment</b>
            </div>
            <div className="col">{getSubsegmentLabel(member.subsegment)}</div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Functional Role</b>
            </div>
            <div className="col">{getFunctionalRoleLabel(member.functionalRole)}</div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Organization Level</b>
            </div>
            <div className="col">{getOrganizationLevelLabel(member.organizationLevel)}</div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Join Date</b>
            </div>
            <div className="col">
              <Datetime at={member.joinDate} format="MMMM D, YYYY" />
            </div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Educ. Hours</b>
            </div>
            <div className="col">{member.vars.educationHours}</div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>CEU Credits</b>
            </div>
            <div className="col">{member.vars.ceuCredits ? Number(member.vars.ceuCredits).toFixed(2) : '0.0'}</div>
          </div>

          <div className="row" style={{ marginTop: 20 }}>
            <div className="col-4 text-right">
              <b>Created</b>
            </div>
            <div className="col">
              <Datetime at={member.createAt} /> by <MemberLink member={entities.member[member.createId!]} />
            </div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Updated</b>
            </div>
            <div className="col">
              <Datetime at={member.updateAt} /> by <MemberLink member={entities.member[member.updateId!]} />
            </div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Last Login</b>
            </div>
            <div className="col">
              <Datetime at={member.loginAt} />
            </div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Member Profile</b>
            </div>
            <div className="col">
              {profileUrl && (
                <a href={profileUrl} target="_blank">
                  {profileUrl}
                </a>
              )}
              {!profileUrl && <span>None</span>}
            </div>
          </div>

          <div className="row">
            <div className="col-4 text-right">
              <b>Tags</b>
            </div>
            <div className="col-6">
              <EditableTags
                tags={this.props.appState.entities.tag}
                tagIds={member.tagIds!}
                onSave={onUpdateTags}
                canEdit={acl.hasPermission(this.props.appState, UserPermissions.Permissions.EDIT_MEMBER_TAGS)}
              />
            </div>
          </div>
          {canViewBadges(Feature.badges, this.props.appState.config, this.props.appState.user) ? (
            <div className="row">
              <div className="col-4 text-right">
                <b>Badges</b>
              </div>
              <div className="col-6">
                <EditableTags
                  tags={this.props.appState.entities.tag}
                  tagIds={member.assignedTags ? member.assignedTags.map(t => t.tagId!) : []}
                  type={Tag.Type.BADGE}
                  onSave={onUpdateAssignedTags}
                  canEdit={acl.hasPermission(this.props.appState, UserPermissions.Permissions.EDIT_MEMBER_TAGS)}
                  label="Edit Badges"
                />
              </div>
            </div>
          ) : null}
          <div className="row">
            <div className="col-4 text-right">
              <b>Chapter History</b>
            </div>
            <div className="col-6">
              {this.props.chapterPositions ? this.pluckChapterHistory(this.props.chapterPositions) : ''}
            </div>
          </div>
          <div className="row">
            <div className="col-4 text-right">
              <b>Internal Tags</b>
            </div>
            <div className="col-6">
              <EditableTags
                tags={this.props.appState.entities.tag}
                tagIds={member.internalTagIds!}
                type={Tag.Type.INTERNAL}
                onSave={onUpdateInternalTags}
                canEdit={acl.hasPermission(this.props.appState, UserPermissions.Permissions.EDIT_MEMBER_TAGS)}
              />
            </div>
          </div>

          {canViewBadges(Feature.badges, this.props.appState.config, this.props.appState.user) &&
            (member.vars.badgeMeetings || member.vars.badgeMeetingsVirtual) && (
              <div className="row">
                <div className="col-4 text-right">
                  <b>Badge Credits</b>
                </div>
                <div className="col">
                  {member.vars.badgeMeetings ? member.vars.badgeMeetings : 0} in-person,{' '}
                  {member.vars.badgeMeetingsVirtual ? member.vars.badgeMeetingsVirtual : 0} virtual
                </div>
              </div>
            )}
          {member.vars.gradDate && (
            <div className="row">
              <div className="col-4 text-right">
                <b>Grad Date</b>
              </div>
              <div className="col">
                <Datetime at={member.vars.gradDate} format="MMM D YYYY" />
              </div>
            </div>
          )}
          {member.vars.kbis && (
            <div className="row">
              <div className="col-4 text-right">
                <b>KBIS Code</b>
              </div>
              <div className="col">{member.vars.kbis}</div>
            </div>
          )}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Users/UsersDetailInfo.tsx